import React, { useState } from "react";
import { Member, Team } from "../types/Types";
import { Box, Typography, Collapse, useMediaQuery, useTheme } from "@mui/material";
import { masters, mastersFonts, tabularNums } from "../theme/mastersTheme";
import MyTeam from "./MyTeam";

interface LeaderboardProps {
  teams: Team[];
  owners: Member[];
  teamsRevealed: boolean;
}

const formatScore = (score: number) => {
  if (score === 0) return 'E';
  return score > 0 ? `+${score}` : `${score}`;
};

const scoreColor = (score: number) => {
  if (score < 0) return masters.pimentoSoft;
  if (score === 0) return masters.ivory;
  return masters.ivoryMuted;
};

const positionColor = (index: number) => {
  if (index === 0) return masters.championBright;
  if (index === 1) return masters.silver;
  if (index === 2) return masters.bronze;
  return masters.ivoryMuted;
};

const Leaderboard: React.FC<LeaderboardProps> = ({ teams, owners, teamsRevealed }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [openTeam, setOpenTeam] = useState<string | null>(null);

  const handleToggle = (teamId: string) => {
    setOpenTeam(openTeam === teamId ? null : teamId);
  };

  const headerCellSx = {
    fontFamily: mastersFonts.body,
    fontSize: '0.65rem',
    fontWeight: 700,
    letterSpacing: '0.18em',
    textTransform: 'uppercase' as const,
    color: masters.champion,
    py: 1.25,
    px: { xs: 0.75, sm: 1.5 },
  };

  return (
    <Box
      sx={{
        border: `1px solid ${masters.ivoryLine}`,
        backgroundColor: 'rgba(11, 61, 46, 0.6)',
        overflow: 'hidden',
      }}
    >
      {/* Header row */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: isMobile
            ? '36px 1fr 56px 52px'
            : '48px minmax(0, 2fr) minmax(0, 1.2fr) 80px 80px 60px',
          alignItems: 'center',
          borderBottom: `1px solid ${masters.champion}`,
          backgroundColor: 'rgba(8, 42, 32, 0.85)',
        }}
      >
        <Typography sx={{ ...headerCellSx, textAlign: 'center' }}>Pos</Typography>
        <Typography sx={headerCellSx}>Team</Typography>
        {!isMobile && <Typography sx={headerCellSx}>Manager</Typography>}
        <Typography sx={{ ...headerCellSx, textAlign: 'right' }}>Total</Typography>
        <Typography sx={{ ...headerCellSx, textAlign: 'right', pr: { xs: 1.5, sm: 0 } }}>Holes Left</Typography>
        {!isMobile && <Typography sx={{ ...headerCellSx, textAlign: 'right', pr: 1.5 }}>$</Typography>}
      </Box>

      {teams.map((team: Team, index: number) => {
        const isOpen = openTeam === team.id;
        const managerName = owners.find(owner => owner.id === team.owner_id)?.nickName ?? 'who dis';
        const prizePlace = index < 3;

        return (
          <Box key={team.id}>
            <Box
              onClick={() => handleToggle(team.id)}
              sx={{
                display: 'grid',
                gridTemplateColumns: isMobile
                  ? '36px 1fr 56px 52px'
                  : '48px minmax(0, 2fr) minmax(0, 1.2fr) 80px 80px 60px',
                alignItems: 'center',
                cursor: 'pointer',
                borderBottom: `1px solid ${masters.ivoryLine}`,
                backgroundColor: isOpen
                  ? 'rgba(201, 169, 97, 0.08)'
                  : index % 2 === 0
                    ? 'transparent'
                    : masters.ivoryStripe,
                transition: 'background-color 0.15s ease',
                '&:hover': {
                  backgroundColor: 'rgba(201, 169, 97, 0.12)',
                },
                py: { xs: 1.25, sm: 1.5 },
                px: { xs: 0.5, sm: 1 },
              }}
            >
              {/* Position */}
              <Box sx={{ textAlign: 'center' }}>
                <Typography
                  sx={{
                    fontFamily: mastersFonts.display,
                    fontWeight: prizePlace ? 700 : 600,
                    fontSize: prizePlace ? '1.5rem' : '1.15rem',
                    fontStyle: prizePlace ? 'italic' : 'normal',
                    color: positionColor(index),
                    lineHeight: 1,
                    ...tabularNums,
                  }}
                >
                  {index + 1}
                </Typography>
              </Box>

              {/* Team name */}
              <Box sx={{ minWidth: 0, pr: 1 }}>
                <Typography
                  sx={{
                    fontFamily: mastersFonts.display,
                    fontWeight: 700,
                    fontSize: { xs: '0.95rem', sm: '1.15rem' },
                    color: masters.ivory,
                    lineHeight: 1.15,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {team.name}
                </Typography>
                {isMobile && (
                  <Typography
                    sx={{
                      fontFamily: mastersFonts.body,
                      fontSize: '0.65rem',
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      color: masters.ivoryDim,
                      mt: 0.25,
                    }}
                  >
                    {managerName}
                  </Typography>
                )}
              </Box>

              {/* Manager (desktop only) */}
              {!isMobile && (
                <Typography
                  sx={{
                    fontFamily: mastersFonts.body,
                    fontSize: '0.8rem',
                    color: masters.ivoryMuted,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    pr: 1,
                  }}
                >
                  {managerName}
                </Typography>
              )}

              {/* Total score */}
              <Typography
                sx={{
                  fontFamily: mastersFonts.body,
                  fontWeight: 700,
                  fontSize: { xs: '1.05rem', sm: '1.35rem' },
                  color: scoreColor(team.total_score),
                  textAlign: 'right',
                  ...tabularNums,
                }}
              >
                {formatScore(team.total_score)}
              </Typography>

              {/* Thru */}
              <Typography
                sx={{
                  fontFamily: mastersFonts.body,
                  fontSize: { xs: '0.75rem', sm: '0.85rem' },
                  color: masters.ivoryMuted,
                  textAlign: 'right',
                  pr: { xs: 1.5, sm: 0 },
                  ...tabularNums,
                }}
              >
                {team.holes_left === 0 ? 'F' : team.holes_left}
              </Typography>

              {/* Prize indicator */}
              {!isMobile && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    gap: 0.25,
                    pr: 1.5,
                  }}
                >
                  {[0, 1, 2].slice(0, 3 - index).map(i => (
                    <Box
                      key={i}
                      sx={{
                        width: 4,
                        height: 14,
                        backgroundColor: index < 3 ? masters.champion : 'transparent',
                      }}
                    />
                  ))}
                </Box>
              )}
            </Box>

            <Collapse in={isOpen}>
              <Box
                sx={{
                  borderTop: `1px solid ${masters.champion}`,
                  borderBottom: `1px solid ${masters.ivoryLine}`,
                  backgroundColor: 'rgba(8, 42, 32, 0.7)',
                  px: { xs: 1, sm: 2 },
                  py: { xs: 1.5, sm: 2 },
                }}
              >
                <MyTeam team={team} teamsRevealed={teamsRevealed} />
              </Box>
            </Collapse>
          </Box>
        );
      })}
    </Box>
  );
};

export default Leaderboard;
